import { Lightbulb, Wrench, Trash2, Phone, ArrowRight, Hospital } from "lucide-react";

/**
 * "Qui contacter ?" section — explains which contact channel to use for which
 * type of issue. Placed between the boîte à idées and emergency numbers so
 * residents instantly orient themselves.
 */
export default function QuiContacterSection() {
  return (
    <section className="bg-cream-50 border-t border-cream-200" data-testid="qui-contacter">
      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="text-center mb-8 sm:mb-10">
          <div className="phenix-eyebrow mb-3 inline-flex items-center gap-1.5">
            <Phone size={11} strokeWidth={2.5} /> Orientation
          </div>
          <h2 className="heading-display text-[1.8rem] sm:text-[2.4rem] text-plum-900 leading-[1.1]">
            Qui contacter selon le besoin ?
          </h2>
          <p className="mt-3 text-[15px] text-plum-700/70 max-w-2xl mx-auto">
            La bonne porte d&apos;entrée, c&apos;est gagner du temps. Voici comment orienter votre demande.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          {/* Boîte à idées */}
          <a href="#boite-idees" className="phenix-card phenix-card-hover p-5 group flex flex-col h-full border-l-4 border-pink-400" data-testid="contact-boite-idees">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-11 h-11 rounded-2xl bg-pink-500 text-white grid place-items-center shrink-0 shadow-sm">
                <Lightbulb size={19} strokeWidth={2.2} />
              </div>
              <div className="flex-1">
                <div className="phenix-pill bg-pink-100 text-pink-600 mb-1 text-[10px]">Suggestion · Avis</div>
                <h3 className="font-display font-bold text-lg text-plum-900 leading-tight">Boîte à idées</h3>
                <div className="text-[11px] text-plum-700/55 font-bold mt-0.5">Sur Istres &amp; Moi · gratuit</div>
              </div>
            </div>
            <ul className="text-[13px] text-plum-700/80 space-y-1 mb-3 leading-snug">
              <li>• Idée pour améliorer la ville</li>
              <li>• Suggestion d&apos;activité, d&apos;événement</li>
              <li>• Avis citoyen, témoignage</li>
              <li>• Question à la mairie</li>
            </ul>
            <span className="mt-auto inline-flex items-center gap-1.5 text-[13px] font-bold text-pink-500 group-hover:gap-2 transition-all">
              Déposer une idée <ArrowRight size={13} />
            </span>
          </a>

          {/* Allo Travaux */}
          <a href="tel:0800373373" className="phenix-card phenix-card-hover p-5 group flex flex-col h-full border-l-4 border-sun-400" data-testid="contact-allo-travaux">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-11 h-11 rounded-2xl bg-sun-400 text-plum-900 grid place-items-center shrink-0 shadow-sm">
                <Wrench size={19} strokeWidth={2.2} />
              </div>
              <div className="flex-1">
                <div className="phenix-pill bg-sun-100 text-sun-700 mb-1 text-[10px]">Voirie · Espaces publics</div>
                <h3 className="font-display font-bold text-lg text-plum-900 leading-tight">Allô Travaux Istres</h3>
                <div className="text-[11px] text-plum-700/55 font-bold mt-0.5 tabular-nums">0 800 373 373 · gratuit</div>
              </div>
            </div>
            <ul className="text-[13px] text-plum-700/80 space-y-1 mb-3 leading-snug">
              <li>• Lampadaire éteint, feu tricolore en panne</li>
              <li>• Nid-de-poule, voirie dégradée</li>
              <li>• Mobilier urbain cassé (banc, panneau)</li>
              <li>• Espace vert à entretenir</li>
            </ul>
            <span className="mt-auto inline-flex items-center gap-1.5 text-[13px] font-bold text-sun-700 group-hover:gap-2 transition-all">
              Appeler maintenant <ArrowRight size={13} />
            </span>
          </a>

          {/* Métropole AMP — encombrants */}
          <a href="tel:0800940494" className="phenix-card phenix-card-hover p-5 group flex flex-col h-full border-l-4 border-mint-500" data-testid="contact-metropole">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-11 h-11 rounded-2xl bg-mint-500 text-white grid place-items-center shrink-0 shadow-sm">
                <Trash2 size={19} strokeWidth={2.2} />
              </div>
              <div className="flex-1">
                <div className="phenix-pill bg-mint-100 text-mint-600 mb-1 text-[10px]">Déchets · Encombrants</div>
                <h3 className="font-display font-bold text-lg text-plum-900 leading-tight">Métropole Aix-Marseille</h3>
                <div className="text-[11px] text-plum-700/55 font-bold mt-0.5 tabular-nums">0 800 94 04 94 · gratuit</div>
              </div>
            </div>
            <ul className="text-[13px] text-plum-700/80 space-y-1 mb-3 leading-snug">
              <li>• Enlèvement d&apos;encombrants à domicile</li>
              <li>• Dépôt sauvage signalé</li>
              <li>• Conteneur poubelle endommagé</li>
              <li>• Question collecte sélective</li>
            </ul>
            <span className="mt-auto inline-flex items-center gap-1.5 text-[13px] font-bold text-mint-600 group-hover:gap-2 transition-all">
              Appeler maintenant <ArrowRight size={13} />
            </span>
          </a>

          {/* Urgences */}
          <a href="#ressources" className="phenix-card phenix-card-hover p-5 group flex flex-col h-full border-l-4 border-pink-600" data-testid="contact-urgences">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-11 h-11 rounded-2xl bg-pink-600 text-white grid place-items-center shrink-0 shadow-sm">
                <Hospital size={19} strokeWidth={2.2} />
              </div>
              <div className="flex-1">
                <div className="phenix-pill bg-pink-100 text-pink-600 mb-1 text-[10px]">Urgence vitale</div>
                <h3 className="font-display font-bold text-lg text-plum-900 leading-tight">Numéros d&apos;urgence</h3>
                <div className="text-[11px] text-plum-700/55 font-bold mt-0.5 tabular-nums">15 · 17 · 18 · 112</div>
              </div>
            </div>
            <ul className="text-[13px] text-plum-700/80 space-y-1 mb-3 leading-snug">
              <li>• Urgence médicale, blessure grave</li>
              <li>• Agression, vol, accident</li>
              <li>• Incendie, fuite de gaz</li>
              <li>• Numéro européen unique : 112</li>
            </ul>
            <span className="mt-auto inline-flex items-center gap-1.5 text-[13px] font-bold text-pink-600 group-hover:gap-2 transition-all">
              Voir tous les numéros <ArrowRight size={13} />
            </span>
          </a>
        </div>

        <p className="text-[12px] text-plum-700/55 text-center mt-6 leading-relaxed max-w-xl mx-auto">
          💡 En cas de doute, déposez votre demande dans la boîte à idées — la mairie vous oriente vers le bon service sous 48 h.
        </p>
      </div>
    </section>
  );
}
